'use client';

/**
 * App-wide client state: who is signed in, and which diagram is open.
 *
 * Both live here rather than in each page because the diagram is edited from
 * five different surfaces (canvas, right rail, generator, architect, Terraform
 * editor) and every one of them needs the *same* spec object -- passing a
 * stale copy to /edit would silently revert another panel's change.
 *
 * The open diagram is mirrored into sessionStorage so a hard refresh, or the
 * full page load the browser does when following an external link, does not
 * drop the user back to an empty canvas. sessionStorage (not local) is
 * deliberate: a diagram belongs to a session, and a second tab should be able
 * to hold a different one.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import api, { ApiError, normalizeDiagram } from './api';
import type { CurrentUser, DiagramResponse } from './types';

const DIAGRAM_KEY = 'archlyy.diagram';

/* ── Auth ─────────────────────────────────────────────────────────────── */

interface AuthState {
  user: CurrentUser | null;
  /** True until the first /api/me settles -- guards against a sign-in flash. */
  loading: boolean;
  signIn: (username: string, password: string) => Promise<CurrentUser>;
  signUp: (email: string, password: string, name: string) => Promise<{ pending: boolean }>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

/* ── Diagram ──────────────────────────────────────────────────────────── */

interface DiagramState {
  diagram: DiagramResponse | null;
  /** The prompt that produced the open diagram, for the Prompt panel. */
  prompt: string;
  setDiagram: (d: DiagramResponse | null, prompt?: string) => void;
  /** Merges a fresh render into place, keeping the prompt unless given one. */
  applyResult: (d: DiagramResponse, prompt?: string) => void;
  clearDiagram: () => void;
}

/** Which slide-in panel is open, if any. */
export type FlyoutId =
  'history' | 'terram' | 'architect' | 'repos' | 'repos-workspace' | 'deploy' | null;

interface FlyoutState {
  flyout: FlyoutId;
  setFlyout: (id: FlyoutId) => void;
}

type AppState = AuthState & DiagramState & FlyoutState;

const AppContext = createContext<AppState | null>(null);

interface PersistedDiagram {
  diagram: DiagramResponse;
  prompt: string;
}

function readPersisted(): PersistedDiagram | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(DIAGRAM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedDiagram;
    // A blob written by an older build may be missing `spec`, which every
    // consumer dereferences. Treat a shape mismatch as "nothing stored", and
    // backfill the optional region/draft fields the same way a freshly loaded
    // history entry is backfilled.
    if (!parsed?.diagram?.spec?.nodes) return null;
    return { diagram: normalizeDiagram(parsed.diagram), prompt: parsed.prompt ?? '' };
  } catch {
    return null;
  }
}

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [diagram, setDiagramState] = useState<DiagramResponse | null>(null);
  const [flyout, setFlyout] = useState<FlyoutId>(null);
  const [prompt, setPrompt] = useState('');
  const hydrated = useRef(false);

  // Restore the open diagram before the first paint of any consumer.
  useEffect(() => {
    const persisted = readPersisted();
    if (persisted) {
      setDiagramState(persisted.diagram);
      setPrompt(persisted.prompt);
    }
    hydrated.current = true;
  }, []);

  const refresh = useCallback(async () => {
    try {
      setUser(await api.auth.me());
    } catch (err) {
      // 401 is the ordinary signed-out case, not an error worth surfacing.
      if (!(err instanceof ApiError && err.isUnauthenticated)) {
        console.error('Could not load the current user', err);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const persist = useCallback((next: DiagramResponse | null, nextPrompt: string) => {
    if (typeof window === 'undefined') return;
    try {
      if (next) {
        window.sessionStorage.setItem(
          DIAGRAM_KEY,
          JSON.stringify({ diagram: next, prompt: nextPrompt })
        );
      } else {
        window.sessionStorage.removeItem(DIAGRAM_KEY);
      }
    } catch {
      // Quota errors are survivable -- the diagram stays in memory, it just
      // will not outlive a refresh.
    }
  }, []);

  const setDiagram = useCallback(
    (next: DiagramResponse | null, nextPrompt?: string) => {
      const resolvedPrompt = nextPrompt ?? (next ? prompt : '');
      setDiagramState(next);
      setPrompt(resolvedPrompt);
      persist(next, resolvedPrompt);
    },
    [persist, prompt]
  );

  const applyResult = useCallback(
    (next: DiagramResponse, nextPrompt?: string) => setDiagram(next, nextPrompt),
    [setDiagram]
  );

  const clearDiagram = useCallback(() => setDiagram(null, ''), [setDiagram]);

  const signIn = useCallback(async (username: string, password: string) => {
    await api.auth.login(username, password);
    const me = await api.auth.me();
    setUser(me);
    setLoading(false);
    return me;
  }, []);

  const signUp = useCallback(async (email: string, password: string, name: string) => {
    const res = await api.auth.register(email, password, name);
    // Self-registration may land in the admin approval queue instead of
    // creating a session -- app/main.py decides, we just report which.
    if (res.status === 'pending') return { pending: true };
    try {
      const me = await api.auth.me();
      setUser(me);
      setLoading(false);
      return { pending: false };
    } catch {
      return { pending: true };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.auth.logout();
    } finally {
      setUser(null);
      setDiagramState(null);
      setPrompt('');
      persist(null, '');
    }
  }, [persist]);

  const value = useMemo<AppState>(
    () => ({
      user,
      loading,
      signIn,
      signUp,
      signOut,
      refresh,
      diagram,
      prompt,
      setDiagram,
      applyResult,
      clearDiagram,
      flyout,
      setFlyout,
    }),
    [
      user,
      loading,
      signIn,
      signUp,
      signOut,
      refresh,
      diagram,
      prompt,
      setDiagram,
      applyResult,
      clearDiagram,
      flyout,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside <AppStateProvider>');
  return ctx;
}

export const useAuth = () => {
  const { user, loading, signIn, signUp, signOut, refresh } = useApp();
  return { user, loading, signIn, signUp, signOut, refresh };
};

export const useDiagram = () => {
  const { diagram, prompt, setDiagram, applyResult, clearDiagram } = useApp();
  return { diagram, prompt, setDiagram, applyResult, clearDiagram };
};
